import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import API_URL from "../../src/Config";
import {
  EXCAVATION_PERMIT_LABEL,
  findExcavationPermitType,
} from "./excavationApprovalSetupUtils";

const isEnabled = (value) => value === 1 || value === "1" || value === true;

function ExcavationDepartmentApprovalSetup({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [permitTypeId, setPermitTypeId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const containerRef = useRef(null);

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const fetchSetup = useCallback(async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const permitResponse = await axios.get(
        `${API_URL}/api/get_work_permit_types`,
        { headers: { Authorization: authKey } }
      );

      const excavationPermit = findExcavationPermitType(
        permitResponse.data?.work_permit_types || []
      );

      if (!excavationPermit) {
        setPermitTypeId("");
        setDepartments([]);
        setFilteredDepartments([]);
        setSeverity("error");
        showSnackbar("Excavation permit type was not found.");
        return;
      }

      setPermitTypeId(excavationPermit.id);

      const setupResponse = await axios.get(
        `${API_URL}/api/permit/departments/setup/${excavationPermit.id}`,
        { headers: { Authorization: authKey } }
      );

      const setupDepartments = setupResponse.data?.departments || [];
      setDepartments(setupDepartments);
      setFilteredDepartments(setupDepartments);
    } catch (error) {
      console.error("Error fetching excavation approval setup:", error.message);
      setSeverity("error");
      showSnackbar("Unable to fetch department approval setup.");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchSetup();
  }, [fetchSetup]);

  useEffect(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) {
      setFilteredDepartments(departments);
      return;
    }

    setFilteredDepartments(
      departments.filter((department) =>
        [
          department.department_name,
          department.approval_order,
          isEnabled(department.is_required) ? "required" : "optional",
          isEnabled(department.is_parallel) ? "parallel" : "sequential",
        ]
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [searchInput, departments]);

  const handleRefresh = () => {
    setSearchInput("");
    fetchSetup();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleEditClick = () => {
    if (!permitTypeId) {
      setSeverity("error");
      showSnackbar("Excavation permit type was not found.");
      return;
    }
    navigate(`/edit-excavation-department-approval-setup/${permitTypeId}`);
  };

  const handleReviewerEditClick = (departmentId) => {
    navigate(`/edit-excavation-reviewer-configuration/${departmentId}`);
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Excavation Department Approval Setup</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                {departments.length === 0 && (
                  <Link
                    to="/add-excavation-department-approval-setup"
                    className="data-list-new"
                  >
                    <Tooltip title="New Setup" arrow>
                      <button className="new-button">
                        <HiOutlinePlus className="new-plus-icon" /> New
                      </button>
                    </Tooltip>
                  </Link>
                )}

                <Link
                  to="/excavation-reviewer-configuration"
                  className="data-list-new"
                >
                  <Tooltip title="Reviewer Configuration" arrow>
                    <button className="new-button">Reviewers</button>
                  </Tooltip>
                </Link>

                {permitTypeId && departments.length > 0 && (
                  <Tooltip title="Edit Setup" arrow>
                    <button className="new-button" onClick={handleEditClick}>
                      <MdOutlineModeEditOutline /> Edit
                    </button>
                  </Tooltip>
                )}

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : filteredDepartments.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. No.</th>
                        <th>PERMIT TYPE</th>
                        <th>DEPARTMENT</th>
                        <th className="center-alignment">APPROVAL ORDER</th>
                        <th className="center-alignment">REQUIRED</th>
                        <th className="center-alignment">PARALLEL</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {filteredDepartments.map((department, index) => (
                        <tr key={department.id || department.department_id}>
                          <td className="center-alignment">{index + 1}</td>
                          <td>{EXCAVATION_PERMIT_LABEL}</td>
                          <td>{department.department_name || "NA"}</td>
                          <td className="center-alignment">
                            {department.approval_order || "NA"}
                          </td>
                          <td className="center-alignment">
                            {isEnabled(department.is_required) ? "Yes" : "No"}
                          </td>
                          <td className="center-alignment">
                            {isEnabled(department.is_parallel) ? "Yes" : "No"}
                          </td>
                          <td className="edit-pop center-alignment">
                            <button
                              className="button-bg"
                              onClick={() =>
                                handleReviewerEditClick(department.department_id)
                              }
                              style={{ marginLeft: "6px" }}
                            >
                              Reviewers
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No Excavation Department Approval Setup Found
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ExcavationDepartmentApprovalSetup;
